import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Ban,
  ChevronDown,
  ChevronRight,
  Loader2,
  PanelRight,
  PlusCircle,
  RotateCw,
  Search,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { Pill, toneFor } from '@/components/ui/pill';
import { Pagination } from '@/components/ui/pagination';
import { Combobox } from '@/components/ui/combobox';
import { FilterField } from '@/components/ui/filter-field';
import { ActiveFilters } from '@/components/ui/active-filters';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import {
  DataTable,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { PageHeader } from '@/components/ui/page-header';
import { OutreacherPicker } from '@/components/money/OutreacherPicker';
import {
  useAdminInviter,
  useAdminInviters,
  useAdminPayoutDetail,
  useAdminPayouts,
  useCancelPayout,
  useCheckPayoutSync,
  useResubmitPayout,
  type PayoutsFilters,
} from '@/hooks/useAdmin';
import { PaymentHistoryTab } from './PaymentHistoryTab';
import { useUrlState } from '@/hooks/useUrlState';
import { useInvalidateMoney } from '@/hooks/useInvalidateMoney';
import { usePendingKeys } from '@/hooks/usePendingKeys';
import { payoutStatusLabel, providerLabel, titleCase } from '@/lib/labels';
import { formatDate, formatDateTime, formatWeekRange } from '@/lib/dates';
import { formatMoney } from '@/lib/money';
import {
  PAYOUT_PROVIDERS,
  PAYOUT_STATUSES,
  canCancelPayout,
  canRetryPayout,
  clampPage,
  inviteLink,
  payoutsQueryParams,
} from '@/lib/moneyPages';
import type { OutreachPayout } from '@/api/types';
import { ManualPayoutDialog } from './ManualPayoutDialog';

const PAGE_SIZE = 50;

type InviterRef = { name: string; publicId: string };

/**
 * Every payout, where it was sent and where it stands. Rows don't change
 * what anyone is paid directly: off-platform payments go through "Record
 * off-platform payment" (header), per-invite write-offs through Adjust owed
 * amount on Invites, and the per-invite ledger lives in the invite's payment
 * history.
 *
 * What a row can do: Check provider status (read-only), Retry send
 * (re-fires the provider call for a failed or unconfirmed payout) and
 * Cancel / Void (frees the payout's invites for a new one).
 */
export function AdminPayouts(): JSX.Element {
  const [f, set] = useUrlState({ status: '', inviter: '', provider: '', page: 1 });
  const [manualOpen, setManualOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const page = Math.max(1, f.page);
  const params = payoutsQueryParams({ ...f, page, limit: PAGE_SIZE });
  const payouts = useAdminPayouts({
    page: params.page as number,
    limit: params.limit as number,
    ...(params.status ? { status: params.status as PayoutsFilters['status'] } : {}),
    ...(params.inviterId ? { inviterId: Number(params.inviterId) } : {}),
    ...(params.providerKey ? { providerKey: params.providerKey as PayoutsFilters['providerKey'] } : {}),
  });
  const inviters = useAdminInviters({ limit: 200 });
  const filteredInviter = useAdminInviter(params.inviterId ? Number(params.inviterId) : null);
  const resubmit = useResubmitPayout();
  const checkSync = useCheckPayoutSync();
  const cancel = useCancelPayout();
  const invalidateMoney = useInvalidateMoney();
  // Per-payout in-flight actions: one mutation hook only tracks its latest call.
  const rowPending = usePendingKeys();

  const inviterById = useMemo(() => {
    const map = new Map<number, InviterRef>();
    for (const i of inviters.data ?? []) map.set(i.id, { name: i.name, publicId: i.publicId });
    return map;
  }, [inviters.data]);

  const rows = payouts.data?.data ?? [];
  const meta = payouts.data?.meta ?? { page, limit: PAGE_SIZE, total: rows.length };

  // Never sit on a page past the end after a filter shrinks the total.
  const loadedMeta = payouts.data?.meta;
  useEffect(() => {
    if (!loadedMeta || payouts.isPlaceholderData) return;
    const clamped = clampPage(page, loadedMeta.total, loadedMeta.limit);
    if (clamped !== page) set({ page: clamped });
  }, [loadedMeta, page, payouts.isPlaceholderData, set]);

  const openPayout = openId ? rows.find((p) => p.publicId === openId) ?? null : null;

  function onRetry(payout: OutreachPayout): Promise<void> {
    return rowPending
      .run(`retry:${payout.publicId}`, () => resubmit.mutateAsync(payout.publicId))
      .then((next) => {
        if (!next) return;
        invalidateMoney();
        toast.success(`Sent again: ${payoutStatusLabel(next.status)} via ${providerLabel(next.providerKey)}`);
      })
      .catch((err: Error) => {
        toast.error(err.message);
      });
  }

  function onCheck(payout: OutreachPayout): void {
    void rowPending
      .run(`check:${payout.publicId}`, () => checkSync.mutateAsync(payout.publicId))
      .then((result) => {
        if (!result) return;
        const provider = providerLabel(result.providerKey);
        if (!result.synced) {
          toast.warning(`${provider} has no record of this payout yet${result.reason ? `: ${result.reason}` : '.'}`);
          return;
        }
        const theirs = result.provider?.status ?? '';
        const matches = result.provider ? providerStatusMatchesLocal(theirs, result.localStatus) : true;
        toast.success(
          `${provider} says ${titleCase(theirs) || 'nothing'}${
            matches ? '' : `, which doesn't match ours (${payoutStatusLabel(result.localStatus)})`
          }`,
        );
      })
      .catch((err: Error) => toast.error(err.message));
  }

  function onCancel(payout: OutreachPayout, reason: string): Promise<void> {
    const wasFailed = payout.status === 'FAILED';
    return rowPending
      .run(`cancel:${payout.publicId}`, () => cancel.mutateAsync({ publicId: payout.publicId, reason }))
      .then((done) => {
        if (!done) return;
        invalidateMoney();
        toast.success(
          wasFailed
            ? 'Payout voided. Its invites can go on a new payout.'
            : 'Payout cancelled. Its invites can go on a new payout.',
        );
      })
      .catch((err: Error) => {
        toast.error(err.message);
      });
  }

  const nameFor = (id: number): string | null =>
    inviterById.get(id)?.name ?? (filteredInviter.data?.id === id ? filteredInviter.data.name : null);

  const actions = (payout: OutreachPayout, inSheet = false): ReactNode => (
    <PayoutActions
      payout={payout}
      outreacherName={nameFor(payout.inviterId)}
      onRetry={() => onRetry(payout)}
      onCheck={() => onCheck(payout)}
      onCancel={(reason) => onCancel(payout, reason)}
      retryPending={rowPending.has(`retry:${payout.publicId}`)}
      checkPending={rowPending.has(`check:${payout.publicId}`)}
      cancelPending={rowPending.has(`cancel:${payout.publicId}`)}
      variant={inSheet ? 'outline' : 'ghost'}
    />
  );

  const activeFilters = [
    f.status
      ? { key: 'status', label: `Status: ${payoutStatusLabel(f.status)}`, clear: () => set({ status: '', page: 1 }) }
      : null,
    f.inviter
      ? {
          key: 'inviter',
          label: `Outreacher: ${filteredInviter.data?.name ?? inviterById.get(Number(f.inviter))?.name ?? '…'}`,
          clear: () => set({ inviter: '', page: 1 }),
        }
      : null,
    f.provider
      ? { key: 'provider', label: `Provider: ${providerLabel(f.provider)}`, clear: () => set({ provider: '', page: 1 }) }
      : null,
  ].filter((x): x is { key: string; label: string; clear: () => void } => x !== null);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payouts"
        description={
          <>
            Every payout, where it was sent and where it stands. Payments made outside the
            platform go through Record off-platform payment; write-offs go through Adjust owed
            amount on Invites.
          </>
        }
        actions={
          <Button size="sm" onClick={() => setManualOpen(true)}>
            <PlusCircle className="h-3 w-3" /> Record off-platform payment
          </Button>
        }
      />
      <ManualPayoutDialog open={manualOpen} onOpenChange={setManualOpen} />

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Status">
          {(['', ...PAYOUT_STATUSES] as const).map((s) => (
            <Button
              key={s || 'all'}
              size="sm"
              variant={f.status === s ? 'default' : 'outline'}
              aria-pressed={f.status === s}
              onClick={() => set({ status: s, page: 1 })}
            >
              {s === '' ? 'All' : payoutStatusLabel(s)}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <FilterField label="Outreacher">
            <OutreacherPicker
              className="w-full md:w-60"
              value={f.inviter}
              onChange={(v) => set({ inviter: v, page: 1 })}
              allowAll
              allLabel="All outreachers"
            />
          </FilterField>
          <FilterField label="Provider">
            <Combobox
              className="w-full md:w-44"
              value={f.provider}
              onChange={(v) => set({ provider: v, page: 1 })}
              options={PAYOUT_PROVIDERS.map((p) => ({ value: p, label: providerLabel(p) }))}
              allowAll
              allLabel="All providers"
            />
          </FilterField>
        </div>
        <ActiveFilters
          filters={activeFilters}
          onClearAll={() => set({ status: '', inviter: '', provider: '', page: 1 })}
        />
      </div>

      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {payouts.isLoading || payouts.isPlaceholderData
          ? 'Loading'
          : `${meta.total.toLocaleString()} payout${meta.total === 1 ? '' : 's'}`}
      </p>

      {payouts.isLoading ? (
        <TableSkeleton cols={['w-32', 'w-28', 'w-20', 'w-20', 'w-16', 'w-20', 'w-32', 'w-24']} />
      ) : payouts.error ? (
        <p className="text-sm text-destructive">{(payouts.error as Error).message}</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Wallet className="h-6 w-6" />}
          title="No payouts match."
          hint={
            activeFilters.length > 0
              ? 'Clear a filter above to see more.'
              : 'Nothing has been paid yet.'
          }
        />
      ) : (
        <DataTable
          className={payouts.isPlaceholderData ? 'opacity-60 transition-opacity' : undefined}
          aria-busy={payouts.isPlaceholderData}
        >
          <DataTableHeader>
            <tr>
              <DataTableHeaderCell>Payroll week</DataTableHeaderCell>
              <DataTableHeaderCell>Outreacher</DataTableHeaderCell>
              <DataTableHeaderCell>Provider</DataTableHeaderCell>
              <DataTableHeaderCell>Status</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Total</DataTableHeaderCell>
              <DataTableHeaderCell>Paid at</DataTableHeaderCell>
              <DataTableHeaderCell>Notes</DataTableHeaderCell>
              <DataTableHeaderCell />
            </tr>
          </DataTableHeader>
          <tbody>
            {rows.map((payout) => (
              <DataTableRow
                key={payout.id}
                state={openId === payout.publicId ? 'emphasis' : 'default'}
                className="cursor-pointer"
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('a,button,input,textarea,[role="menuitem"],[role="dialog"],[role="alertdialog"]')) return;
                  setOpenId(payout.publicId);
                }}
              >
                <td className="py-2 font-medium">{formatWeekRange(payout.payrollWeekOf)}</td>
                <td className="py-2" data-label="Outreacher">
                  <OutreacherCell inviterId={payout.inviterId} known={inviterById.get(payout.inviterId)} />
                </td>
                <td className="py-2" data-label="Provider">
                  {providerLabel(payout.providerKey)}
                </td>
                <td className="py-2" data-label="Status">
                  <Pill tone={toneFor(payout.status)}>{payoutStatusLabel(payout.status)}</Pill>
                </td>
                <td className="py-2 text-right" data-label="Total">
                  ${formatMoney(payout.totalAmount)}
                </td>
                <td className="py-2 text-muted-foreground" data-label="Paid at">
                  {formatDate(payout.paidAt)}
                </td>
                <td className="py-2 text-muted-foreground" data-label="Notes">
                  {payout.notes ? (
                    <span className="block max-w-[16rem] truncate" title={payout.notes}>
                      {payout.notes}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="actions py-2 text-right">
                  <div className="flex justify-end gap-1">
                    {actions(payout)}
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Open payout details`}
                      title="Details and invites on this payout"
                      onClick={() => setOpenId(payout.publicId)}
                    >
                      <PanelRight className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </DataTableRow>
            ))}
          </tbody>
        </DataTable>
      )}
      <Pagination
        page={meta.page}
        limit={meta.limit}
        total={meta.total}
        onPageChange={(p) => set({ page: p })}
      />

      <Sheet open={!!openPayout} onOpenChange={(open) => !open && setOpenId(null)}>
        <SheetContent onOpenAutoFocus={(e) => e.preventDefault()}>
          {openPayout && (
            <PayoutSheetBody
              payout={openPayout}
              known={inviterById.get(openPayout.inviterId)}
              actions={actions(openPayout, true)}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/** Name + profile link; looks the outreacher up when the capped list doesn't have them. */
function OutreacherCell({ inviterId, known }: { inviterId: number; known: InviterRef | undefined }): JSX.Element {
  const fetched = useAdminInviter(known ? null : inviterId);
  const ref = known ?? (fetched.data ? { name: fetched.data.name, publicId: fetched.data.publicId } : null);
  if (!ref) return <span className="text-muted-foreground">{fetched.isLoading ? '…' : '—'}</span>;
  return (
    <Link to={`/admin/outreachers/${ref.publicId}`} className="hover:underline">
      {ref.name}
    </Link>
  );
}

function PayoutActions({
  payout,
  outreacherName,
  onRetry,
  onCheck,
  onCancel,
  retryPending,
  checkPending,
  cancelPending,
  variant,
}: {
  payout: OutreachPayout;
  outreacherName: string | null;
  onRetry: () => Promise<void>;
  onCheck: () => void;
  onCancel: (reason: string) => Promise<void>;
  retryPending: boolean;
  checkPending: boolean;
  cancelPending: boolean;
  variant: 'ghost' | 'outline';
}): JSX.Element {
  // Only meaningful once the provider has given us a reference to ask about.
  const canCheck = !!payout.providerExternalId;
  const canRetry = canRetryPayout(payout.status);
  const canCancel = canCancelPayout(payout.status);
  const isFailedVoid = payout.status === 'FAILED';
  const provider = providerLabel(payout.providerKey);
  const amount = `$${formatMoney(payout.totalAmount)}`;
  const who = outreacherName ?? 'this outreacher';

  return (
    <>
      {canCheck && (
        <Button
          size="sm"
          variant={variant}
          onClick={onCheck}
          disabled={checkPending}
          title={`Ask ${provider} what it has on record. Changes nothing.`}
        >
          {checkPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />} Check provider status
        </Button>
      )}
      {canRetry && (
        <ConfirmDialog
          title={`Retry sending ${amount} to ${who}?`}
          description={
            <>
              Asks {provider} to send this payout again. It goes with the same reference, so the
              provider can tell it's a retry of this payout rather than a new payment.
            </>
          }
          confirmLabel="Retry send"
          pending={retryPending}
          onConfirm={onRetry}
          trigger={
            <Button
              size="sm"
              variant={variant}
              disabled={retryPending}
              title={`Send this payout to ${provider} again`}
            >
              {retryPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />} Retry send
            </Button>
          }
        />
      )}
      {canCancel && (
        <ConfirmDialog
          title={isFailedVoid ? `Void this failed ${amount} payout?` : `Cancel this ${amount} payout?`}
          description={
            isFailedVoid ? (
              <>
                Clears the failed payout so its invites can go on a new one. If it was paid
                outside the platform, use{' '}
                <span className="font-medium text-foreground">Record off-platform payment</span>{' '}
                for what was actually paid, and{' '}
                <span className="font-medium text-foreground">Adjust owed amount</span> on Invites
                to write off anything waived.
              </>
            ) : (
              <>
                Frees {amount} of invites so they can go on a new payout. {provider} is{' '}
                <span className="font-medium text-foreground">not told</span>; if it already
                started the transfer, settle with them directly.
              </>
            )
          }
          tone="destructive"
          confirmLabel={isFailedVoid ? 'Void payout' : 'Cancel payout'}
          cancelLabel="Keep it"
          reasonRequired
          reasonLabel={isFailedVoid ? 'Why are you voiding it?' : 'Why are you cancelling it?'}
          reasonPlaceholder={
            isFailedVoid
              ? 'e.g. Paid outside the platform through the clipping dashboard.'
              : 'e.g. Duplicate payout, amount disputed. Kept with the payout.'
          }
          pending={cancelPending}
          onConfirm={onCancel}
          trigger={
            <Button
              size="sm"
              variant={variant}
              disabled={cancelPending}
              title={
                isFailedVoid
                  ? 'Void: clears the failed payout so its invites can be paid again'
                  : 'Cancel: frees its invites for a new payout'
              }
              className="text-destructive hover:text-destructive"
            >
              <Ban className="h-3 w-3" /> {isFailedVoid ? 'Void' : 'Cancel'}
            </Button>
          }
        />
      )}
    </>
  );
}

/** `paidVia` for display: retainers are paid off-platform, not via a provider. */
function paidViaLabel(v: string): string {
  return v === 'RETAINER_OFFPLATFORM' ? 'Manual / off-platform' : titleCase(v);
}

function PayoutSheetBody({
  payout,
  known,
  actions,
}: {
  payout: OutreachPayout;
  known: InviterRef | undefined;
  actions: ReactNode;
}): JSX.Element {
  const fetched = useAdminInviter(known ? null : payout.inviterId);
  const ref = known ?? (fetched.data ? { name: fetched.data.name, publicId: fetched.data.publicId } : null);
  // The list row lacks the invite publicIds; the detail fetch adds them so
  // each line item can expand into that invite's payment-event history.
  const detail = useAdminPayoutDetail(payout.publicId);
  const p = detail.data ?? payout;
  const lineItems =
    detail.data?.lineItems ??
    payout.lineItems.map((li) => ({ ...li, invitePublicId: null as string | null }));
  const [expandedInviteId, setExpandedInviteId] = useState<number | null>(null);
  const extra = parseFloat(p.additionalAmount || '0');
  const settled = parseFloat(p.settledAmount || '0');
  const hasWriteOffs = lineItems.some((li) => parseFloat(li.writeOffAmount ?? '0') > 0);
  return (
    <>
      <div className="space-y-3 border-b border-border px-5 pb-4 pt-5">
        <div className="min-w-0 pr-8">
          <SheetTitle className="flex flex-wrap items-center gap-2 text-lg font-semibold">
            <span>
              ${formatMoney(p.totalAmount)} to{' '}
              {ref ? (
                <Link to={`/admin/outreachers/${ref.publicId}`} className="hover:underline">
                  {ref.name}
                </Link>
              ) : (
                'outreacher'
              )}
            </span>
            <Pill tone={toneFor(payout.status)}>{payoutStatusLabel(payout.status)}</Pill>
          </SheetTitle>
          <SheetDescription className="mt-1 text-sm text-muted-foreground">
            Payroll week {formatWeekRange(payout.payrollWeekOf)} · {providerLabel(p.providerKey)} ·{' '}
            {lineItems.length} invite{lineItems.length === 1 ? '' : 's'}
          </SheetDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      </div>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4 text-sm">
        <div className="grid gap-5 sm:grid-cols-2">
          <Section title="Money">
            <Row label="Total" value={`$${formatMoney(p.totalAmount)}`} emphasis />
            {extra !== 0 && <Row label="Extra on top of invites" value={`$${formatMoney(extra)}`} />}
            {settled > 0 && (
              <Row label="Settled (written off)" value={`$${formatMoney(p.settledAmount)}`} />
            )}
            <Row label="Provider" value={providerLabel(p.providerKey)} />
            {p.paidVia && <Row label="Paid via" value={paidViaLabel(p.paidVia)} />}
            {p.externalReceiptId && <Row label="Receipt" value={p.externalReceiptId} mono />}
            {p.providerExternalId && <Row label="Provider reference" value={p.providerExternalId} mono />}
            {settled > 0 && p.status !== 'PAID' && (
              <p className="text-[10px] leading-snug text-muted-foreground">
                The settled amount is written off the invites only when this payout is paid.
                Cancelling it restores the full owed balance.
              </p>
            )}
          </Section>
          <Section title="Timeline">
            <Row label="Created" value={formatDateTime(payout.createdAt)} />
            {payout.submittedAt && <Row label="Sent to payment provider" value={formatDateTime(payout.submittedAt)} />}
            <Row label="Paid" value={formatDateTime(payout.paidAt)} />
            <Row label="Last updated" value={formatDateTime(payout.updatedAt)} />
          </Section>
        </div>
        {payout.notes && (
          <Section title="Notes">
            <p className="whitespace-pre-wrap text-xs leading-relaxed text-foreground/85">{payout.notes}</p>
          </Section>
        )}
        <Section title="Invites on this payout">
          {lineItems.length === 0 ? (
            <p className="text-xs text-muted-foreground">No invites listed on this payout.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-left text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-6 py-1.5" aria-label="Expand" />
                  <th className="py-1.5 pr-2 text-left">Creator</th>
                  <th className="py-1.5 pl-2 text-right">Amount</th>
                  {hasWriteOffs && <th className="py-1.5 pl-2 text-right">Written off</th>}
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li) => {
                  const isExpanded = expandedInviteId === li.inviteId;
                  const canExpand = !!li.invitePublicId;
                  const writeOff = parseFloat(li.writeOffAmount ?? '0');
                  return (
                    <Fragment key={`${li.inviteId}-${li.targetIdentifier}`}>
                      <tr className="border-t border-border/50">
                        <td className="w-6 py-1.5">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedInviteId((cur) => (cur === li.inviteId ? null : li.inviteId))
                            }
                            disabled={!canExpand}
                            className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
                            aria-label={isExpanded ? 'Hide payment history' : 'Show payment history'}
                            aria-expanded={isExpanded}
                            title={
                              canExpand
                                ? "This invite's payment history"
                                : detail.isLoading
                                  ? 'Loading'
                                  : 'Payment history unavailable for this invite'
                            }
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-3 w-3" strokeWidth={2} />
                            ) : (
                              <ChevronRight className="h-3 w-3" strokeWidth={2} />
                            )}
                          </button>
                        </td>
                        <td className="py-1.5 pr-2">
                          <Link
                            to={inviteLink({ handle: li.targetIdentifier, inviterId: payout.inviterId })}
                            className="text-primary hover:underline"
                            title="Find this invite on the Invites page"
                          >
                            {li.targetIdentifier}
                          </Link>
                        </td>
                        <td className="py-1.5 pl-2 text-right tabular-nums">${formatMoney(li.amount)}</td>
                        {hasWriteOffs && (
                          <td className="py-1.5 pl-2 text-right tabular-nums text-muted-foreground">
                            {writeOff > 0 ? `$${formatMoney(li.writeOffAmount)}` : '—'}
                          </td>
                        )}
                      </tr>
                      {isExpanded && li.invitePublicId && (
                        <tr className="border-t border-border/30 bg-muted/20">
                          <td colSpan={hasWriteOffs ? 4 : 3} className="px-2 py-2">
                            <PaymentHistoryTab invitePublicId={li.invitePublicId} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </Section>
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({
  label,
  value,
  mono = false,
  emphasis = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  emphasis?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={`truncate text-right text-foreground/90 ${mono ? 'font-mono text-[11px]' : ''} ${
          emphasis ? 'font-semibold text-foreground' : ''
        }`}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Local status → provider status string, for flagging drift. Kept loose —
 * provider strings vary; we only flag when the categories obviously diverge
 * (paid vs not, failed vs not).
 */
function providerStatusMatchesLocal(
  providerStatus: string,
  localStatus: OutreachPayout['status'],
): boolean {
  const p = providerStatus.toUpperCase();
  if (localStatus === 'PAID') return p === 'PAID';
  if (localStatus === 'FAILED') return p === 'FAILED';
  if (localStatus === 'CANCELLED') return p === 'CANCELLED';
  return p === 'PENDING' || p === 'PROCESSING' || p === 'SUBMITTED';
}
